import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect, useDispatch } from "react-redux";
import {
  Button,
  Input,
  Image,
  Subtitle2Stronger,
  Body1,
  Field,
  Body1Strong,
} from "@fluentui/react-components";

import {
  ArrowLeft24Regular,
  ArrowRight24Regular,
  BuildingMultiple24Regular,
  Eye24Regular,
  EyeOff24Regular,
} from "@fluentui/react-icons";

import { api, clearAuth, getBaseUrl, setTokens } from "../../api/api";
import {
  defaultCatch,
  defaultSuccess,
  setLoading,
} from "../../redux/actions";
import Stack from "../Stack";
import { Types } from "../../redux/actionTypes";
import { INTENT_ERROR } from "../../variables";
import { loginSuccess } from "../../redux/reducers/authSlice";


function Login(props) {
  const dispatch = useDispatch();
  const { defaultCatch, defaultSuccess, setLoading } = props;
 

  const { t } = useTranslation();

  const [slide, setSlide] = useState("login");
  const [email, setEmail] = useState("");
  const [ssoURL, setSsoURL] = useState("");
  const [ssoProvider, setSsoProvider] = useState("");
  const [code, setCode] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectedBaseUrl, setSelectedBaseUrl] = useState("");
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (Office.context.mailbox && Office.context.mailbox.userProfile) {
      const userEmail = Office.context.mailbox.userProfile.emailAddress;
      setEmail(userEmail);
    }
  }, []);

  const setDomain = domain => {
    localStorage.setItem("domain", domain);
  }

  const cleanDomain = () => {
    localStorage.removeItem("domain");
  }  

  const ssoAuth = (data, baseURL) => {
    const { provider = "" } = data;
    const route = `/users/saml/sign_in?web=true&provider=${provider}`;
    return `${baseURL}${route}`;
  };

  const ssoAuthGenerator = useCallback(
    (data, baseUrl) => {
      const url = ssoAuth(data, baseUrl);
      setSsoProvider(data.provider || "");
      setSelectedBaseUrl(baseUrl);
      setSsoURL(url);
    },
    [ssoAuth]
  );

  const openSamlDialog = useCallback(() => {
    const bridgeUrl =
      window.location.origin +
      "/saml-bridge.html" +
      "?tenantUrl=" + encodeURIComponent(selectedBaseUrl) +
      "&provider=" + encodeURIComponent(ssoProvider);

    // Office.context.ui is only injected by the Office host (Outlook).
    // It is undefined when the page is opened directly in a browser.
    if (!Office?.context?.ui?.displayDialogAsync) {
      console.error(
        "Office.context.ui.displayDialogAsync no disponible. " +
        "Asegúrate de que el add-in corre dentro de Outlook (no en un browser directamente)."
      );
      return;
    }

    Office.context.ui.displayDialogAsync(
      bridgeUrl,
      { height: 60, width: 40, requireHTTPS: true },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("SAML dialog error:", asyncResult.error.message);
          return;
        }
        const dialog = asyncResult.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          try {
            const payload = JSON.parse(arg.message);
            if (payload.type !== "saml_success") { dialog.close(); return; }

            const { access_token, client, uid } = payload;
            dialog.close();

            const token = { "access-token": access_token, client, "token-type": "Bearer", uid };
            setTokens(token);

            // The fragment token only authenticates validate_token. That call
            // rotates the access-token and invalidates this one, so the app
            // must not mount until the rotated token is stored.
            setLoading(true);
            api.get("/auth/validate_token")
              .then(({ data }) => {
                const rotated = data?.tokens?.[0]?.token;
                if (rotated?.["access-token"]) setTokens(rotated);
                dispatch(loginSuccess(rotated || token));
                dispatch({ type: Types.SET_AUTH, payload: cleanPayload(data) });
              })
              .catch(() => {
                clearAuth();
                dispatch({ type: "auth/logoutSuccess" });
              })
              .finally(() => setLoading(false));
          } catch (_) { dialog.close(); /* malformed message — ignore */ }
        });

        // 12006 = user closed the dialog manually — no action needed
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (eventArgs) => {
          if (eventArgs.error !== 12006) console.error("SAML dialog event error:", eventArgs.error);
        });
      }
    );
  }, [selectedBaseUrl, ssoProvider, dispatch, setLoading]);

  const cleanPayload = (data) => {
    const token = data.tokens[0] || {};

    const {
      enabled_parking = false,
      enabled_workplace = false,
      enabled_room_reservations = false,
      enabled_manual_checkin = false,
      enabled_parking_reservations_manual_checkin = false,
      enabled_room_reservations_manual_checkin = false,
    } = token;

    return {
      ...data,
      enabled_parking,
      enabled_workplace,
      enabled_room_reservations,
      enabled_manual_checkin,
      enabled_parking_reservations_manual_checkin,
      enabled_room_reservations_manual_checkin,
    };
  };

  const sendCode = (code, email, t) => {
    const params = { code, email };
    setLoading(true)
    api
      .post("/sac/validate", params)
      .then(({ data }) => {
        if (!data.success) return;
        setTokens(data.tokens[0].token);
        dispatch(loginSuccess(data.tokens[0].token))
        dispatch({
          type: Types.SET_AUTH,
          payload: cleanPayload(data),
        });
      })
      .catch((error) => {
        console.log(error);
        setErrorCode(t("Error validating the code, please try again"));
      })
      .finally(() => {
        setLoading(false)
      });
  };


  const resolveAuthType = useCallback((authTypeEntry, baseUrl) => {
    if (authTypeEntry.sso) {
      ssoAuthGenerator(authTypeEntry, baseUrl);
      setSlide("login");
      return;
    }
    setSelectedBaseUrl(baseUrl);
    setSlide("password");
  }, [ssoAuthGenerator]);

  const authType = useCallback(
    () => {
      setLoading(true)
      clearAuth()
      return api.post("/auth/type", { email })
      .then(({ data }) => {
        const { domains = [], auth_types = [] } = data;

        if (domains.length > 1) {
          setTenants(auth_types.map(({ domain, name, sso, provider }) => ({ domain, name, sso, provider })));
          setSlide("tenant");
          return;
        }

        const domain = domains[0];
        setDomain(domain);
        const baseUrl = getBaseUrl(domain);
        const selectedType = auth_types[0] || {};

        if (selectedType.sso || data.sso) {
          return ssoAuthGenerator(selectedType.provider ? selectedType : data, baseUrl);
        }

        setSelectedBaseUrl(baseUrl);
        setSlide("password");
      }).finally(() => {
        setLoading(false)
      })
    },
    [email, ssoAuthGenerator]
  );

  const selectTenant = useCallback((tenant) => {
    setDomain(tenant.domain);
    const baseUrl = getBaseUrl(tenant.domain);
    resolveAuthType(tenant, baseUrl);
  }, [resolveAuthType]);

  const deviseAuth = useCallback(() => {
    setLoading(true)
    api.post("/auth/sign_in", { email, password }, { baseURL: selectedBaseUrl })
      .then(({ data }) => {
        const tokenFromBody = data.tokens?.[0]?.token;
        if (tokenFromBody?.["access-token"]) {
          setTokens(tokenFromBody);
          dispatch(loginSuccess(tokenFromBody));
        }
        dispatch({ type: Types.SET_AUTH, payload: cleanPayload(data) });
      })
      .catch((e) => {
        const errors = e?.response?.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          setPasswordError(errors[0]);
        } else if (e?.response?.status === 401) {
          setPasswordError(t("password is incorrect"));
        } else {
          setPasswordError(t("unknown error"));
        }
      })
      .finally(() => setLoading(false));
  }, [email, password, selectedBaseUrl, t]);

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
    // Reset all derived auth state so the flow re-evaluates for the new address
    setSsoURL("");
    setSsoProvider("");
    setSelectedBaseUrl("");
    setTenants([]);
    clearAuth();
    setSlide("login");
  }, []);

  const handleCode = useCallback((e) => {
    setCode(e.target.value);
    setErrorCode("");
  }, []);


  const handleTypeAuth = useCallback(() => {
    authType()
  }, [authType]);

  const handleEmailKeyPress = useCallback(
    (event) => {
      if (!email || event.key !== "Enter") return;

      if (ssoURL && ssoProvider) openSamlDialog();   // SAML dialog flow
      else if (ssoURL) validateTokenSSO();            // legacy OTP flow
      else handleTypeAuth();
    },
    [email, ssoURL, ssoProvider, openSamlDialog, validateTokenSSO, handleTypeAuth]
  );

  const validateTokenSSO = useCallback(() => {
    const params = { email };
    setLoading(true)
    api.post("/sac/send", params)
    .then(({ data }) => {
      if (data.success) setSlide("code");
    })
    .finally(() => {
      setLoading(false)
    })
  }, [email]);

  const backButton = useCallback(() => {
    setSsoURL("");
    setSsoProvider("");
    clearAuth();
    setCode("");
    setErrorCode("");
    setPassword("");
    setPasswordError("");
    setShowPassword(false);
    setSelectedBaseUrl("");
    setTenants([]);
    setSlide("login");
  }, [setSsoURL]);


  const LoginBox = useCallback(() => {
    return (
      <Stack alignItems="center" gap="16px" style={{ paddingTop: '150px' }}>
        <Image width={35} src="./assets/icon-128.png" />
        <Subtitle2Stronger align="center">{t("we welcome you to the corporate experience")}</Subtitle2Stronger>
        <Body1 align="center">{t("with Corporate Experience you can manage the reservations of services that you will use the days you go to the office.")}</Body1>
        <Stack alignItems="stretch" gap="20px" style={{ width: '100%' }}>
          <Field label={t("email")}>
            <Input
              appearance="filled-darker"
              type="email"
              onChange={handleEmailChange}
              value={email}
              onKeyDown={handleEmailKeyPress}
            />
          </Field>
          {ssoURL && ssoProvider ? (
            <Button
              appearance="primary"
              onClick={openSamlDialog}>
              {t("login with SSO")}
            </Button>
          ) : ssoURL ? (
            <Button
              appearance="primary"
              onClick={validateTokenSSO}>
              SSO
            </Button>
          ) : (
            <Button
              appearance="primary"
              onClick={handleTypeAuth}>
              {t("continue")}
            </Button>
          )}
        </Stack>
        <Button
          appearance="transparent"
          size="small"
          as="a"
          href="https://www.parso.co/contact"
          target="_blank"
        >
          {t("Request access")}
        </Button>
      </Stack >
    );
  }, [backButton, email, ssoURL, ssoProvider, handleEmailChange, handleEmailKeyPress, validateTokenSSO, openSamlDialog, t]);


  const CodeBox = useCallback(() => {
    return (
      <Stack alignItems="center" gap="16px" style={{ paddingTop: '150px' }}>
        <Image width={35} src="./assets/icon-128.png" />
        <Body1Strong align="center"> {t("Enter the code we have sent to your email")}</Body1Strong>
        <Body1 align="center">{t("To enter, write the code that we sent to X email, which will expire in 10 minutes", { email })}</Body1>
        <Field validationMessage={errorCode}>
          <Input
            placeholder={t("Access code")}
            appearance="filled-darker"
            required
            type="password"
            name="code"
            id="code"
            onChange={handleCode}
            showSuccessIndicator={false}
            value={code}
          />
        </Field>
        <Stack direction="row" alignItems="center" gap="16px">
          <Button
            icon={<ArrowLeft24Regular />}
            onClick={backButton}
            aria-label="Back"
          >
            {t("back")}
          </Button>
          <Button
            icon={<ArrowRight24Regular />}
            onClick={() => sendCode(code, email, t)}
            disabled={!code}
            appearance="primary"
            flat
          >
            {t("continue")}
          </Button>
        </Stack>
      </Stack>
    );
  }, [backButton, code, email, handleCode, sendCode, t]);

  const TenantBox = useCallback(() => {
    return (
      <Stack alignItems="center" gap="16px" style={{ paddingTop: '80px' }}>
        <Image width={35} src="./assets/icon-128.png" />
        <Subtitle2Stronger align="center">{t("select your account")}</Subtitle2Stronger>
        <Body1 align="center">{t("which account do you want to log into")}</Body1>
        <Stack alignItems="stretch" gap="8px" style={{ width: '100%', marginTop: '8px' }}>
          {tenants.map((tenant) => (
            <button
              key={tenant.domain}
              onClick={() => selectTenant(tenant)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '14px 16px',
                background: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
            >
              <BuildingMultiple24Regular style={{ color: '#8a8a8a', flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: 600, fontSize: '14px', color: '#242424' }}>
                {tenant.name}
              </span>
              <ArrowRight24Regular style={{ color: '#0078d4', flexShrink: 0 }} />
            </button>
          ))}
        </Stack>
        <Button
          appearance="transparent"
          icon={<ArrowLeft24Regular />}
          onClick={backButton}
          style={{ alignSelf: 'flex-start' }}
        >
          {t("back")}
        </Button>
      </Stack>
    );
  }, [backButton, selectTenant, t, tenants]);

  const forgotPassword = useCallback(() => {
    api.post("/password/reset", { email })
      .then(() => {
        setPasswordError(t("we have sent you an email to x to change your password", { x: email }));
      })
      .catch(() => {
        setPasswordError(t("unknown error"));
      });
  }, [email, t]);

  const PasswordBox = useCallback(() => {
    return (
      <Stack alignItems="center" gap="16px" style={{ paddingTop: '150px' }}>
        <Image width={35} src="./assets/icon-128.png" />
        <Subtitle2Stronger align="center">{t("we welcome you to the corporate experience")}</Subtitle2Stronger>
        <Body1 align="center">{email}</Body1>
        <Stack alignItems="stretch" gap="20px" style={{ width: '100%' }}>
          <Field
            label={t("password")}
            validationMessage={passwordError}
            validationState={passwordError ? "error" : "none"}
          >
            <Input
              appearance="filled-darker"
              type={showPassword ? "text" : "password"}
              placeholder={t("enter your password")}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                setPasswordError("");
              }}
              onKeyDown={e => {
                if (e.key === "Enter" && password) deviseAuth();
              }}
              contentAfter={
                <Button
                  appearance="transparent"
                  size="small"
                  icon={showPassword ? <EyeOff24Regular /> : <Eye24Regular />}
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? t("hide password") : t("show password")}
                />
              }
            />
          </Field>
          <Button
            appearance="primary"
            onClick={deviseAuth}
            disabled={!password}
          >
            {t("login")}
          </Button>
        </Stack>
        <Stack direction="row" justifyContent="space-between" style={{ width: '100%' }}>
          <Button
            appearance="transparent"
            icon={<ArrowLeft24Regular />}
            onClick={backButton}
          >
            {t("back")}
          </Button>
          <Button
            appearance="transparent"
            onClick={forgotPassword}
          >
            {t("did you forget your password")}
          </Button>
        </Stack>
      </Stack>
    );
  }, [backButton, deviseAuth, email, forgotPassword, password, passwordError, showPassword, t]);

  const getSlide = useCallback(() => {
    if (slide === "login") return LoginBox();
    if (slide === "tenant") return TenantBox();
    if (slide === "code") return CodeBox();
    if (slide === "password") return PasswordBox();
  }, [CodeBox, LoginBox, PasswordBox, TenantBox, slide]);

  return <div className="Login">{getSlide()}</div>;
}

const mapDispatchToProps = {
  setLoading,
  defaultSuccess,
  defaultCatch,
};

export default connect(null, mapDispatchToProps)(Login);
